import { env } from '../config/env.js';

/**
 * Log levels
 */
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

/**
 * Current log level based on environment
 */
const currentLevel = env.isProduction ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

/**
 * Format log message with timestamp
 * @param {string} level - Log level
 * @param {any[]} args - Arguments to log
 * @returns {any[]}
 */
function formatMessage(level, args) {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level}]`;
  return [prefix, ...args];
}

/**
 * Logger utility with environment-aware logging
 */
export const logger = {
  /**
   * Debug level logging (development only)
   * @param  {...any} args - Arguments to log
   */
  debug(...args) {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.debug(...formatMessage('DEBUG', args));
    }
  },

  /**
   * Info level logging
   * @param  {...any} args - Arguments to log
   */
  info(...args) {
    if (currentLevel <= LOG_LEVELS.INFO) {
      console.info(...formatMessage('INFO', args));
    }
  },

  /**
   * Warning level logging
   * @param  {...any} args - Arguments to log
   */
  warn(...args) {
    if (currentLevel <= LOG_LEVELS.WARN) {
      console.warn(...formatMessage('WARN', args));
    }
  },

  /**
   * Error level logging
   * @param  {...any} args - Arguments to log
   */
  error(...args) {
    if (currentLevel <= LOG_LEVELS.ERROR) {
      console.error(...formatMessage('ERROR', args));
    }
  },

  /**
   * Group related log messages
   * @param {string} label - Group label
   * @param {() => void} fn - Function containing grouped logs
   */
  group(label, fn) {
    if (!env.isProduction) {
      console.group(label);
      try {
        fn();
      } finally {
        console.groupEnd();
      }
    }
  },

  /**
   * Time a function execution
   * @param {string} label - Timer label
   * @param {() => any} fn - Function to time
   * @returns {any} Function result
   */
  time(label, fn) {
    if (!env.isProduction) {
      console.time(label);
      try {
        return fn();
      } finally {
        console.timeEnd(label);
      }
    }
    return fn();
  },

  /**
   * Log a table (development only)
   * @param {any} data - Data to display as table
   */
  table(data) {
    if (!env.isProduction) {
      console.table(data);
    }
  }
};

export default logger;
